import { AppConstant } from "../config/AppConstants";
import { getAccessToken, getGuId, getRefreshToken, setRefreshToken, getLoginId, setAccessToken } from "../utils/SecurityService";
import { BASE_URL, GetRefreshToken_api } from "../config/ApiConfig";
import { dispatchInactiveUserEvent, isInactiveUserResponse } from "../utils/inactiveUserHandler.js";

let refreshTokenRequest = null;

const isValidStoredValue = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  value !== "null" &&
  value !== "undefined";

const hasRefreshContext = () =>
  isValidStoredValue(getRefreshToken()) &&
  isValidStoredValue(getGuId()) &&
  isValidStoredValue(getLoginId());

const buildAuthorizedHeaders = (headers = {}, accessToken = getAccessToken()) => {
  if (!isValidStoredValue(accessToken)) {
    return { ...headers };
  }

  return {
    ...headers,
    Authorization: `Bearer ${accessToken}`,
  };
};

const clearSessionAndRedirect = () => {
  sessionStorage.clear();
  localStorage.clear();
  window.location.href = "/";
};

const throwInactiveUserError = (payload) => {
  dispatchInactiveUserEvent(payload);

  const error = new Error(
    payload?.Data?.ErrorMessage || "This user is currently inactive."
  );
  error.name = "InactiveUserError";
  error.response = payload;
  throw error;
};

const refreshAccessToken = async () => {
  const refreshResponse = await fetch(BASE_URL + GetRefreshToken_api, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      AppVersion: AppConstant.AppVersion,
      RefreshToken: getRefreshToken(),
      Language: AppConstant.Language,
      DeviceId: AppConstant.DeviceID,
      OsVersion: AppConstant.OsVersion,
      ClientId: AppConstant.DEFAULT_CLIENT_ID,
      GuId: getGuId(),
      ManufactureModel: AppConstant.ManufactureModel,
      GrantType: "User",
    }),
  });

  if (!refreshResponse.ok) {
    console.log("Refresh token failed -> logout");
    clearSessionAndRedirect();
    throw new Error("Refresh token failed");
  }

  const refreshData = await refreshResponse.json();

  const { AccessToken, RefreshToken } = refreshData.Data.Records;

  setAccessToken(AccessToken);
  setRefreshToken(RefreshToken);

  console.log("Token refreshed successfully");

  return { accessToken: AccessToken, refreshToken: RefreshToken };
};

const getOrCreateRefreshTokenRequest = async () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = refreshAccessToken().finally(() => {
      refreshTokenRequest = null;
    });
  }

  return refreshTokenRequest;
};

export async function loginPost(url, requestParams) {
  console.log("POST CALLED:", url, requestParams);
  console.log("Using Access Token:", getAccessToken());
  const headers = buildAuthorizedHeaders({ "Content-Type": "application/json" });
  const currentGuId = getGuId();
  if (currentGuId) requestParams.GuId = currentGuId;

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestParams),
  });

  console.log("POST raw response for url:", url, response);

  if (!response.ok) {
    const errorBody = await response.text();
    console.error("POST ERROR BODY:", errorBody);
    throw new Error("Request failed: " + response.status);
  }

  const payload = await response.json();

  /* if (isInactiveUserResponse(payload)) {
    throwInactiveUserError(payload);
  } */

  return payload;
}

export const post = async ({
  url,
  method = "POST",
  params = {},
}) => {
  // Common params for all APIs
  const finalParams = {
    ...params,
    GuId: getGuId(),
  };
  console.log("POST CALLED:", url, finalParams);

  const response = await fetchWithTokenRefresh(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: method === "GET" ? null : JSON.stringify(finalParams),
  });
  console.log("POST raw response for url:", url, response);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API failed (${response.status}): ${errorText}`);
  }

  const payload = await response.json();

  /* if (isInactiveUserResponse(payload)) {
    throwInactiveUserError(payload);
  } */

  return payload;
};

// This function only knows how to retry fetch when token expires
export const fetchWithTokenRefresh = async (url, options = {}, retry = true) => {

  if (!hasRefreshContext()) {
    window.location.href = "/";
    throw new Error("Tokens missing");
  }

  let currentAccessToken = getAccessToken();

  if (!isValidStoredValue(currentAccessToken)) {
    const refreshedTokens = await getOrCreateRefreshTokenRequest();
    currentAccessToken = refreshedTokens.accessToken;
  }

  let response = await fetch(url, {
    ...options,
    headers: buildAuthorizedHeaders(options.headers || {}, currentAccessToken),
  });

  // ✅ If not unauthorized → return
  if (response.status !== 401) {
    return response;
  }

  // ❌ Prevent infinite loop
  if (!retry) {
    clearSessionAndRedirect();
    throw new Error("Unauthorized");
  }

  console.log("Access token expired. Refreshing...");

  const refreshedTokens = await getOrCreateRefreshTokenRequest();

  return fetchWithTokenRefresh(
    url,
    {
      ...options,
      headers: buildAuthorizedHeaders(options.headers || {}, refreshedTokens.accessToken),
    },
    false
  );

  // 🔁 Refresh Token API
  const refreshResponse = await fetch(BASE_URL + GetRefreshToken_api, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      AppVersion: AppConstant.AppVersion,
      RefreshToken: getRefreshToken(),
      Language: AppConstant.Language,
      DeviceId: AppConstant.DeviceID,
      OsVersion: AppConstant.OsVersion,
      ClientId: AppConstant.DEFAULT_CLIENT_ID,
      GuId: getGuId(),
      ManufactureModel: AppConstant.ManufactureModel,
      GrantType: "User",
    }),
  });

  if (!refreshResponse.ok) {
    console.log("Refresh token failed → logout");
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/";
    throw new Error("Refresh token failed");
  }

  const refreshData = await refreshResponse.json();

  const { AccessToken, RefreshToken } = refreshData.Data.Records;

  setAccessToken(AccessToken);
  setRefreshToken(RefreshToken);

  console.log("Token refreshed successfully");

  // 🔁 Retry original request with new token
  return fetchWithTokenRefresh(url, options, false);
};

